import * as React from "react";
import type {
    ILineStatusCurrentOrder,
    ILineStatusItem,
    ILineStatusLiveCurrentOrder,
    LineStatusValue,
} from "@/lib/backend/manufacturing2.generated.types";
import { niceDateTime } from "@/lib/util";
import ErrorComponent, { ErrorType } from "../ui/ErrorComponent";
import { translate } from "../IntlProviderWrapper";
import { compact } from "es-toolkit";
import StatusDot from "./StatusDot";
import { getLineStatusDisplay } from "./lineStatusDisplay";

import "./LineStatusDetails.scss";

type Props = {
    status: ILineStatusItem;
};

/** Semantic CSS custom properties from vars.scss (--color-*). */
const LINE_STATUS_FILL: Record<LineStatusValue, string> = {
    stopped: "var(--color-error)",
    idle: "var(--color-warning)",
    working: "var(--color-success)",
    in_preparation: "var(--color-info)",
    unknown: "var(--color-neutral)",
};

const STALE_OPACITY = 0.5;

const MACHINE_STATUS_LABELS: Record<LineStatusValue, { id: string; defaultMessage: string }> = {
    working: { id: "LineStatusDetails.machine_status.working", defaultMessage: "Working" },
    idle: { id: "LineStatusDetails.machine_status.idle", defaultMessage: "Idle" },
    stopped: { id: "LineStatusDetails.machine_status.stopped", defaultMessage: "Stopped" },
    in_preparation: { id: "LineStatusDetails.machine_status.in_preparation", defaultMessage: "In preparation" },
    unknown: { id: "LineStatusDetails.machine_status.unknown", defaultMessage: "Unknown" },
};

function formatMachineStatus(status: LineStatusValue): string {
    const label = MACHINE_STATUS_LABELS[status] ?? MACHINE_STATUS_LABELS.unknown;
    return translate(label.id, label.defaultMessage);
}

function formatIsoTimestamp(timestamp: string): string {
    const date = new Date(timestamp);
    return Number.isNaN(date.getTime()) ? timestamp : niceDateTime(date);
}

function getMachineExternalId(live: ILineStatusItem["live"]): string | null {
    if (live.machineExternalId) {
        return live.machineExternalId;
    }
    if (live.machineExternalIds && live.machineExternalIds.length > 0) {
        return live.machineExternalIds.join(", ");
    }
    return null;
}

function hasLiveInferredOrderMismatch(
    liveOrder: ILineStatusLiveCurrentOrder,
    inferredOrder: ILineStatusCurrentOrder
): boolean {
    const workOrderMismatch =
        liveOrder.workOrderId != undefined &&
        liveOrder.workOrderId !== "" &&
        liveOrder.workOrderId !== inferredOrder.orderExternalId;
    const processMismatch =
        liveOrder.processNum != undefined &&
        liveOrder.processNum !== "" &&
        liveOrder.processNum !== inferredOrder.processNum;

    return workOrderMismatch || processMismatch;
}

const LineStatusDetails = ({ status }: Props) => {
    const { live } = status;
    const display = getLineStatusDisplay(status);
    const operation = status.inferredOperation;
    const running = operation?.running ?? false;
    const downtime = operation?.downtime;
    const liveOrder = live.currentOrder;
    const inferredOrder = status.inferredProduction?.currentOrder;
    const machineExternalId = getMachineExternalId(live);
    const standstillCode = live.tags?.standstillCode;
    const liveInferredMismatch =
        liveOrder != undefined && inferredOrder != undefined && hasLiveInferredOrderMismatch(liveOrder, inferredOrder);

    const cards = [
        {
            title: (
                <>
                    <StatusDot
                        r={15}
                        className="mb-1"
                        fill={LINE_STATUS_FILL[display.status] ?? LINE_STATUS_FILL.unknown}
                        opacity={display.stale ? STALE_OPACITY : 1}
                    />
                    {translate("common.status", "Status")}
                </>
            ),
            data: [
                {
                    title: "",
                    pairs: compact([
                        {
                            key: translate("common.active", "Active"),
                            value: running ? translate("common.yes", "Yes") : translate("common.no", "no"),
                        },
                        live.statusText && {
                            key: translate("LineStatusDetails.live_status", "Live status"),
                            value: live.statusText,
                        },
                        live.statusTimestamp && {
                            key: translate("LineStatusDetails.status_time", "Status time"),
                            value: formatIsoTimestamp(live.statusTimestamp),
                        },
                        display.stale && {
                            key: translate("LineStatusDetails.stale", "Stale"),
                            value: translate("common.yes", "Yes"),
                        },
                        standstillCode != undefined && {
                            key: translate("LineStatusDetails.standstill_code", "Standstill code"),
                            value: String(standstillCode),
                        },
                        live.source && {
                            key: translate("LineStatusDetails.source", "Source"),
                            value: live.source,
                        },
                        machineExternalId && {
                            key: translate("LineStatusDetails.machine", "Machine"),
                            value: machineExternalId,
                        },
                        {
                            key: translate("LineStatusDetails.machine_status", "Machine status"),
                            value: formatMachineStatus(display.status),
                        },
                        live.worker && {
                            key: translate("LineStatusDetails.current_operator", "Current operator"),
                            value: live.worker,
                        },
                        {
                            key: translate("common.downtime", "Downtime"),
                            value:
                                downtime != undefined ? translate("common.yes", "Yes") : translate("common.no", "no"),
                        },
                        downtime != undefined && {
                            key: translate("LineDetails.downtime_severity", "Downtime severity"),
                            value: String(downtime.severity),
                        },
                        downtime != undefined && {
                            key: translate("LineDetails.downtime_title", "Downtime title"),
                            value: downtime.description,
                        },
                    ]),
                },
            ],
        },
        {
            title: translate("LineDetails.active_operation", "Active operation"),
            data: compact([
                liveOrder && {
                    title: translate("LineStatusDetails.reported_from_machine", "Reported from machine"),
                    pairs: compact([
                        liveOrder.workOrderId && {
                            key: translate("LineStatusDetails.work_order_id", "Work order ID"),
                            value: liveOrder.workOrderId,
                        },
                        liveOrder.operationTitle && {
                            key: translate("LineStatusDetails.operation_title", "Operation title"),
                            value: liveOrder.operationTitle,
                        },
                        liveOrder.processNum && {
                            key: translate("common.operation", "Operation"),
                            value: liveOrder.processNum,
                        },
                    ]),
                },
                inferredOrder && {
                    title: translate("LineStatusDetails.inferred_by_leap", "Inferred by LEAP"),
                    pairs: [
                        {
                            key: translate("common.material", "Material"),
                            value: `[${inferredOrder.materialExternalId}] ${inferredOrder.materialTitle}`,
                        },
                        {
                            key: translate("common.order", "Order"),
                            value: inferredOrder.orderExternalId,
                        },
                        {
                            key: translate("common.operation", "Operation"),
                            value: `(${inferredOrder.processNum}) ${inferredOrder.orderTitle}`,
                        },
                        {
                            key: translate("LineDetails.quantity_produced_total", "Quantity produced / total"),
                            value: `${inferredOrder.quantityProduced} / ${inferredOrder.quantityTotal} ${inferredOrder.measurementUnit}`,
                        },
                    ],
                },
            ]),
            error: liveInferredMismatch && {
                type: "warning",
                message: translate(
                    "LineStatusDetails.discrepancy.live_inferred",
                    "Reported order from the machine differs from the active order inferred by LEAP."
                ),
            },
        },
    ];

    return (
        <div className="line-status-details">
            <div className="card-deck">
                {cards.map((card, cardIndex) => (
                    <div key={cardIndex} className="card">
                        <div className="card-header text-uppercase bg-transparent text-nowrap d-flex align-items-center gap-2">
                            {card.title}
                        </div>
                        <div className="card-body">
                            {card.data.length === 0 ? translate("common.not_available", "Not available") : undefined}
                            {card.data.map((section, sectionIndex) => (
                                <React.Fragment key={sectionIndex}>
                                    {section.title ? (
                                        <div className="text-uppercase small text-muted mt-1 mb-1">{section.title}</div>
                                    ) : undefined}
                                    <dl className="property-list">
                                        {section.pairs.map(pair => (
                                            <div key={pair.key}>
                                                <dt>{pair.key}</dt>
                                                <dd>{pair.value}</dd>
                                            </div>
                                        ))}
                                    </dl>
                                </React.Fragment>
                            ))}
                        </div>
                        {card.error ? (
                            <ErrorComponent
                                className="m-3"
                                type={card.error.type as ErrorType}
                                msg={card.error.message}
                            />
                        ) : undefined}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default LineStatusDetails;
